'use client';

import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import { useCallback, useState } from 'react';
import type { Resolver } from 'react-hook-form';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
	Button,
	Form,
	FormGroup,
	Label,
	Modal,
	ModalBody,
	ModalFooter,
} from 'reactstrap';
import CustomInputField from 'src/components/shared/form-fields/custom-input-field';
import ButtonWithLoader from 'src/components/shared/form-fields/loading-button';
import { handleErrorMessages } from 'src/helpers/utils';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux-hooks';
import {
	fetchJobActivityLog,
	markResidentResponsibility,
	updateResidentResponsibility,
} from 'src/redux/jobs/api';
import { updateVisibility } from 'src/redux/modals/slice';
import type { ThunkRejectValue } from 'src/types/api';
import * as Yup from 'yup';

type ResponsibilityType = 'partially' | 'yes' | 'no';

interface ResidentResponsibilityFormData {
	responsibility: ResponsibilityType;
	reason: string;
	reasonForChange: string;
}

export interface ResidentResponsibilityModalProps {
	mode: 'mark' | 'edit';
	rootNodeId: number;
	jobId?: number;
	reason?: string;
	residentCauseStatus?: 'partially' | 'yes';
}

const MAX_CHARS = 500;

const reasonSchema = Yup.string()
	.trim()
	.required('This is a required field')
	.max(MAX_CHARS, `Maximum ${MAX_CHARS} characters allowed`);

const validationSchema = Yup.object().shape({
	responsibility: Yup.string()
		.oneOf(['partially', 'yes'])
		.required('Please select a responsibility type'),
	reason: reasonSchema,
	reasonForChange: Yup.string().when('$isEditMode', ([isEditMode], schema) =>
		isEditMode
			? schema
					.trim()
					.required('This is a required field')
					.max(MAX_CHARS, `Maximum ${MAX_CHARS} characters allowed`)
			: schema.optional(),
	),
});

const ResidentResponsibilityModal = () => {
	const [isLoading, setIsLoading] = useState(false);
	const dispatch = useAppDispatch();
	const { extraProps } = useAppSelector((state: Record<string, unknown>) => {
		const modals = state?.modals as Record<string, unknown>;
		return modals?.residentResponsibilityModal as Record<string, unknown>;
	});
	const residentCaused = useAppSelector(
		(state) => state?.jobDetails?.data?.resident_caused,
	);

	const srId = useAppSelector((state) => state?.jobDetails?.slideOut?.srId);
	const slideOutType = useAppSelector(
		(state) => state?.jobDetails?.slideOut?.type,
	);
	const props = extraProps as ResidentResponsibilityModalProps;
	const isEditMode = props?.mode === 'edit';
	const rootNodeId = props?.rootNodeId;
	const jobId = props?.jobId;
	const reason = props?.reason;
	const residentCauseStatus = props?.residentCauseStatus;

	const methods = useForm<ResidentResponsibilityFormData>({
		mode: 'all',
		context: { isEditMode },
		defaultValues: {
			responsibility:
				residentCauseStatus ??
				(isEditMode && jobId
					? (residentCaused?.find((caused) => caused.job_id === jobId)?.value ??
						undefined)
					: undefined),
			reason:
				reason ||
				(isEditMode && jobId
					? (residentCaused?.find((caused) => caused.job_id === jobId)
							?.reason ?? '')
					: ''),
			reasonForChange: '',
		},
		resolver: yupResolver(validationSchema) as unknown as Resolver<ResidentResponsibilityFormData>,
	});

	const {
		control,
		handleSubmit,
		formState: { isValid },
	} = methods;

	const watchedResponsibility = useWatch({ control, name: 'responsibility' });

	const handleClose = useCallback(() => {
		dispatch(
			updateVisibility({
				modalType: 'residentResponsibilityModal',
				isVisible: false,
			}),
		);
	}, [dispatch]);

	const onSubmit = (formData: ResidentResponsibilityFormData) => {
		setIsLoading(true);
		const payload = {
			value: formData.responsibility,
			reason: formData.reason,
			...(isEditMode && { reason_for_change: formData?.reasonForChange }),
		};
		dispatch(
			isEditMode
				? updateResidentResponsibility(jobId)(payload)
				: markResidentResponsibility(rootNodeId)(payload),
		)
			.unwrap()
			.then((res) => {
				toast((res as { message?: string })?.message, { type: 'success' });
				dispatch(
					fetchJobActivityLog(srId)({
						...(slideOutType === 'jobDetails' ? { job_id: rootNodeId } : {}),
					}),
				);
				window.dispatchEvent(
					new CustomEvent('resident-responsibility-updated', {
						detail: {
							jobId: rootNodeId,
							is_resident_caused_problem: formData.responsibility,
							resident_problem_description: [formData.reason],
						},
					}),
				);
				handleClose();
			})
			.catch((error: ThunkRejectValue) => {
				handleErrorMessages(
					error?.data?.errors ?? error?.errors ?? error?.message ?? null,
					(msg: string) => toast(msg, { type: 'error' }),
				);
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	return (
		<Modal
			isOpen
			toggle={handleClose}
			centered
			modalClassName="common-confirmation-modal"
		>
			<div className="modal-header">
				<h5 className="modal-title">Resident Responsibility</h5>
				<button
					type="button"
					className="btn-close"
					aria-label="Close"
					onClick={handleClose}
				/>
			</div>
			<FormProvider {...methods}>
				<Form onSubmit={handleSubmit(onSubmit)}>
					<ModalBody>
						{isEditMode && (
							<div className="body-sm mb-4">
								{residentCaused?.[0]?.reason_for_change ? 'Edited' : 'Set'} by{' '}
								{residentCaused?.[0]?.set_by_type === 'admin' ? 'Lula' : 'You'}{' '}
								on {moment.utc(residentCaused?.[0]?.set_at).format('MMM DD')}
							</div>
						)}
						<FormGroup>
							<div className="body-sm mb-2">Responsibility of the Resident<span className="text-danger"> *</span></div>
							<FormGroup check className="form-group mt-2">
								<CustomInputField
									name="responsibility"
									type="radio"
									id="responsibility-partial"
									checked={watchedResponsibility === 'partially'}
									value="partially"
								/>
								<Label check for="responsibility-partial">
									Partial Responsibility
								</Label>
							</FormGroup>
							<FormGroup check className="form-group mt-2">
								<CustomInputField
									name="responsibility"
									type="radio"
									id="responsibility-full"
									checked={watchedResponsibility === 'yes'}
									value="yes"
								/>
								<Label check for="responsibility-full">
									Full Responsibility
								</Label>
							</FormGroup>
						</FormGroup>
						<FormGroup className="form-group">
							<Label for="reason">
								Reason for Responsibility
								<span className="text-danger"> *</span>
							</Label>
							<CustomInputField
								name="reason"
								type="textarea"
								placeholder="Describe why resident is responsible..."
							/>
						</FormGroup>
						{isEditMode && (
							<FormGroup className="form-group no-margin">
								<Label for="reasonForChange">
									Reason for Change
									<span className="text-danger"> *</span>
								</Label>
								<CustomInputField
									name="reasonForChange"
									type="textarea"
									placeholder="Describe why you're changing this..."
								/>
							</FormGroup>
						)}
					</ModalBody>
					<ModalFooter>
						<Button
							color="none"
							className="secondary-btn"
							type="button"
							onClick={handleClose}
						>
							Cancel
						</Button>
						<ButtonWithLoader
							color="none"
							className="primary-btn"
							type="submit"
							isLoading={isLoading}
							disabled={!isValid}>
							{isEditMode ? 'Save Changes' : 'Save'}
						</ButtonWithLoader>
					</ModalFooter>
				</Form>
			</FormProvider>
		</Modal>
	);
};

export default ResidentResponsibilityModal;
